import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { parse, PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { fetchSimilarSellers } from '../../util/api';

const { UUID } = sdkTypes;
export const PROFILE_LISTING_SORTS = ['createdAt', '-createdAt', '-price', 'price'];
export const DEFAULT_PROFILE_LISTING_SORT = PROFILE_LISTING_SORTS[0];
// Custom variants only — avoid Sharetribe square-small center-crops that cut heads.
// fit:scale keeps the full portrait (Sharetribe allows scale|crop); CSS top-frames the square.
export const PROFILE_HEADER_AVATAR_VARIANTS = [
  'profile-header-avatar-sm',
  'profile-header-avatar',
  'profile-header-avatar-2x',
];
// Portrait bounding box (taller than wide) so top-framing has headroom in the square UI.
const PROFILE_HEADER_AVATAR_ASPECT = 4 / 3;
const PROFILE_HEADER_AVATAR_FIT = { fit: 'scale' };

export const getValidProfileListingSort = sort =>
  PROFILE_LISTING_SORTS.includes(sort) ? sort : DEFAULT_PROFILE_LISTING_SORT;

// ================ Async Thunks ================ //

///////////////
// Show User //
///////////////
const showUserPayloadCreator = ({ userId, config }, { dispatch, rejectWithValue, extra: sdk }) => {
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': PROFILE_HEADER_AVATAR_VARIANTS.map(variant => `variants.${variant}`),
      ...createImageVariantConfig(
        'profile-header-avatar-sm',
        146,
        PROFILE_HEADER_AVATAR_ASPECT,
        PROFILE_HEADER_AVATAR_FIT
      ),
      ...createImageVariantConfig(
        'profile-header-avatar',
        292,
        PROFILE_HEADER_AVATAR_ASPECT,
        PROFILE_HEADER_AVATAR_FIT
      ),
      ...createImageVariantConfig(
        'profile-header-avatar-2x',
        584,
        PROFILE_HEADER_AVATAR_ASPECT,
        PROFILE_HEADER_AVATAR_FIT
      ),
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showUserThunk = createAsyncThunk('ProfilePage/showUser', showUserPayloadCreator);

// Backward compatible wrapper for the thunk
export const showUser = (userId, config) => dispatch => {
  return dispatch(showUserThunk({ userId, config }));
};

/////////////////////////
// Query User Listings //
/////////////////////////
const queryUserListingsPayloadCreator = (
  { userId, config, ownProfileOnly = false, sort = DEFAULT_PROFILE_LISTING_SORT },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  const listingSort = getValidProfileListingSort(sort);

  const queryParams = {
    sort: listingSort,
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'images'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  const listingsPromise = ownProfileOnly
    ? sdk.ownListings.query({
        states: ['published'],
        ...queryParams,
      })
    : sdk.listings.query({
        author_id: userId,
        ...queryParams,
      });

  return listingsPromise
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listings = response.data.data;
      const listingRefs = listings
        .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
        .map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      return { listingRefs, response };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryUserListingsThunk = createAsyncThunk(
  'ProfilePage/queryUserListings',
  queryUserListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserListings = (
  userId,
  config,
  ownProfileOnly = false,
  sort = DEFAULT_PROFILE_LISTING_SORT
) => dispatch => {
  return dispatch(queryUserListingsThunk({ userId, config, ownProfileOnly, sort }));
};

//////////////////////////
// Query User's Reviews //
//////////////////////////
const queryUserReviewsPayloadCreator = ({ userId }, { rejectWithValue, extra: sdk }) => {
  return sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      return reviews;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryUserReviewsThunk = createAsyncThunk(
  'ProfilePage/queryUserReviews',
  queryUserReviewsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserReviews = userId => dispatch => {
  return dispatch(queryUserReviewsThunk({ userId }));
};

///////////////////////////
// Query Similar Sellers //
///////////////////////////
const querySimilarSellersPayloadCreator = ({ userId, config }, { dispatch, rejectWithValue }) => {
  return fetchSimilarSellers({ userId: userId.uuid })
    .then(apiResponse => {
      const response = { data: apiResponse };
      const users = apiResponse.data || [];
      const similarSellerRefs = users.map(({ id, type }) => ({ id, type }));
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return { similarSellerRefs, response };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const querySimilarSellersThunk = createAsyncThunk(
  'ProfilePage/querySimilarSellers',
  querySimilarSellersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const querySimilarSellers = (userId, config) => dispatch => {
  return dispatch(querySimilarSellersThunk({ userId, config }));
};

// ================ Slice ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  similarSellerRefs: [],
  userShowError: null,
  queryListingsError: null,
  querySimilarSellersInProgress: false,
  querySimilarSellersError: null,
  reviews: [],
  queryReviewsError: null,
};

const profilePageSlice = createSlice({
  name: 'ProfilePage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // showUser cases
      .addCase(showUserThunk.pending, (state, action) => {
        state.userShowError = null;
        state.userId = action.meta.arg.userId;
      })
      .addCase(showUserThunk.fulfilled, state => {
        // No state changes needed on success
      })
      .addCase(showUserThunk.rejected, (state, action) => {
        state.userShowError = storableError(action.payload);
      })
      // queryUserListings cases
      .addCase(queryUserListingsThunk.pending, (state, action) => {
        const userId = action.meta.arg.userId;
        // Empty listings only when user id changes
        state.userListingRefs = userId === state.userId ? state.userListingRefs : [];
        state.queryListingsError = null;
      })
      .addCase(queryUserListingsThunk.fulfilled, (state, action) => {
        state.userListingRefs = action.payload.listingRefs;
      })
      .addCase(queryUserListingsThunk.rejected, (state, action) => {
        state.userListingRefs = [];
        state.queryListingsError = storableError(action.payload);
      })
      // queryUserReviews cases
      .addCase(queryUserReviewsThunk.pending, state => {
        state.queryReviewsError = null;
      })
      .addCase(queryUserReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(queryUserReviewsThunk.rejected, (state, action) => {
        state.reviews = [];
        state.queryReviewsError = action.payload;
      })
      // querySimilarSellers cases
      .addCase(querySimilarSellersThunk.pending, state => {
        state.querySimilarSellersInProgress = true;
        state.querySimilarSellersError = null;
      })
      .addCase(querySimilarSellersThunk.fulfilled, (state, action) => {
        state.querySimilarSellersInProgress = false;
        state.similarSellerRefs = action.payload.similarSellerRefs;
      })
      .addCase(querySimilarSellersThunk.rejected, (state, action) => {
        state.querySimilarSellersInProgress = false;
        state.similarSellerRefs = [];
        state.querySimilarSellersError = storableError(action.payload);
      });
  },
});

export const { setInitialState, setUserId } = profilePageSlice.actions;
export default profilePageSlice.reducer;

// ================ Load data ================ //

const isCurrentUser = (userId, cu) => userId?.uuid === cu?.id?.uuid;

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);
  const listingSort = getValidProfileListingSort(parse(search)?.sort);
  const isPreviewForCurrentUser = params.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const currentUser = getState()?.user?.currentUser;
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  if (isPreviewForCurrentUser) {
    return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
      if (isCurrentUser(userId, currentUser) && isUserAuthorized(currentUser)) {
        // Scenario: 'active' user somehow tries to open a link for "variant" profile
        return Promise.all([
          dispatch(showUser(userId, config)),
          dispatch(queryUserListings(userId, config, false, listingSort)),
          dispatch(queryUserReviews(userId)),
        ]);
      } else if (isCurrentUser(userId, currentUser)) {
        // Handle a scenario, where user (in pending-approval state)
        // tries to see their own profile page.
        // => just set userId to state
        return dispatch(setUserId(userId));
      } else {
        return Promise.resolve({});
      }
    });
  }

  // Fetch data for plain profile page.
  // Note 1: returns 404s if user is not 'active'.
  // Note 2: In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  // On a private marketplace, show active (approved) current user's own page
  // even if they don't have viewing rights
  const canFetchOwnProfileOnly =
    isPrivateMarketplace &&
    isAuthorized &&
    hasNoViewingRights &&
    isCurrentUser(userId, currentUser);

  if (!canFetchData) {
    return Promise.resolve();
  } else if (canFetchOwnProfileOnly) {
    return Promise.all([
      dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
      dispatch(queryUserListings(userId, config, canFetchOwnProfileOnly, listingSort)),
      dispatch(setUserId(userId)),
    ]);
  }

  return Promise.all([
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
    dispatch(showUser(userId, config)),
    dispatch(queryUserListings(userId, config, false, listingSort)),
    dispatch(queryUserReviews(userId)),
  ]);
};
